import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IdCard, KeyRound, Lock, Save, ShieldCheck, ToggleLeft, ToggleRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const emptyForm = {
  old_password: "",
  new_password: "",
  confirm_password: "",
};

export default function AdminSettings() {
  const nav = useNavigate();
  const { logout, user } = useAuth();
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [platformSettings, setPlatformSettings] = useState({
    identity_verification_required: true,
    emergency_manual_deposit_auto_approve: false,
  });
  const [platformLoading, setPlatformLoading] = useState(true);
  const [platformSaving, setPlatformSaving] = useState(false);

  useEffect(() => {
    let active = true;
    api.get("/admin/settings/platform")
      .then(({ data }) => {
        if (active) {
          setPlatformSettings({
            identity_verification_required: data?.settings?.identity_verification_required !== false,
            emergency_manual_deposit_auto_approve: data?.settings?.emergency_manual_deposit_auto_approve === true,
            emergency_manual_deposit_auto_approve_updated_at: data?.settings?.emergency_manual_deposit_auto_approve_updated_at || "",
            emergency_manual_deposit_auto_approve_updated_by: data?.settings?.emergency_manual_deposit_auto_approve_updated_by || "",
          });
        }
      })
      .catch((error) => toast.error(formatApiError(error)))
      .finally(() => {
        if (active) setPlatformLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const savePlatformSettings = async (nextSettings) => {
    if (
      nextSettings.emergency_manual_deposit_auto_approve === true &&
      platformSettings.emergency_manual_deposit_auto_approve !== true
    ) {
      const confirmed = window.confirm(
        "Emergency mode will auto-approve NEW Chime/Venmo deposits without verifying payment and auto-approve eligible Chime/Venmo withdrawals after 15-45 seconds. Withdrawal funds will be reserved and forwarded to the active payout group; an agent must still send the payment and upload proof. Fake deposits or fraudulent payout details can cause losses. Existing requests are not affected. Turn it on anyway?"
      );
      if (!confirmed) return;
    }
    if (
      nextSettings.identity_verification_required === false &&
      platformSettings.identity_verification_required !== false
    ) {
      const confirmed = window.confirm(
        "Turn OFF ID verification for withdrawals? Unverified customers will be able to submit withdrawal requests. Keep this ON unless you are intentionally disabling KYC."
      );
      if (!confirmed) return;
    }
    setPlatformSaving(true);
    try {
      const { data } = await api.put("/admin/settings/platform", nextSettings);
      setPlatformSettings({
        identity_verification_required: data?.settings?.identity_verification_required !== false,
        emergency_manual_deposit_auto_approve: data?.settings?.emergency_manual_deposit_auto_approve === true,
        emergency_manual_deposit_auto_approve_updated_at: data?.settings?.emergency_manual_deposit_auto_approve_updated_at || "",
        emergency_manual_deposit_auto_approve_updated_by: data?.settings?.emergency_manual_deposit_auto_approve_updated_by || "",
      });
      toast.success(
        nextSettings.emergency_manual_deposit_auto_approve !== platformSettings.emergency_manual_deposit_auto_approve
          ? data?.settings?.emergency_manual_deposit_auto_approve
            ? "Emergency Chime/Venmo deposit auto-approval is ON."
            : "Emergency Chime/Venmo deposit auto-approval is OFF."
          : nextSettings.identity_verification_required !== platformSettings.identity_verification_required
            ? data?.settings?.identity_verification_required === false
              ? "ID verification turned off. Phone verification still applies."
              : "ID verification turned on for withdrawals."
            : "Platform settings saved."
      );
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setPlatformSaving(false);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (form.new_password.length < 6) {
      toast.error("New password must be at least 6 characters.");
      return;
    }
    if (form.new_password !== form.confirm_password) {
      toast.error("New passwords do not match.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/reset-password", {
        old_password: form.old_password,
        new_password: form.new_password,
      });
      setForm(emptyForm);
      toast.success("Password changed. Please sign in again.");
      setTimeout(() => {
        logout();
        nav("/admin", { replace: true });
      }, 500);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-3 sm:p-6 md:p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-white">Settings</h1>
        <p className="mt-1 text-sm text-slate-500">Control platform rules and change your admin login password.</p>
      </div>

      <div className="mb-5 max-w-2xl rounded-lg border border-[#2F6FA5]/30 bg-[#081421] p-5 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-lg border border-[#2F6FA5]/35 bg-[#2F6FA5]/10 text-[#6EA8D8]">
              <IdCard className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-white">ID verification requirement</h2>
              <p className="mt-1 text-sm leading-6 text-slate-400">
                Keep this ON to require Didit ID/selfie before wallet withdrawal.
                Turn OFF only if you intentionally want unverified customers to request cashout.
              </p>
              {platformSettings.identity_verification_required === false && (
                <p className="mt-3 rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-xs font-bold leading-5 text-rose-200">
                  Warning: withdrawal KYC is OFF right now. Customers without approved ID can submit withdrawal requests.
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            disabled={platformLoading || platformSaving}
            onClick={() => savePlatformSettings({
              ...platformSettings,
              identity_verification_required: !(platformSettings.identity_verification_required !== false),
            })}
            className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-black transition disabled:opacity-60 ${
              platformSettings.identity_verification_required !== false
                ? "border border-emerald-400/35 bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25"
                : "border border-amber-400/35 bg-amber-500/15 text-amber-200 hover:bg-amber-500/25"
            }`}
          >
            {platformSettings.identity_verification_required !== false ? (
              <ToggleRight className="h-5 w-5" />
            ) : (
              <ToggleLeft className="h-5 w-5" />
            )}
            {platformLoading
              ? "Loading..."
              : platformSaving
                ? "Saving..."
                : platformSettings.identity_verification_required !== false
                  ? "Withdrawal KYC ON"
                  : "Withdrawal KYC OFF"}
          </button>
        </div>
      </div>

      <div className={`mb-5 max-w-2xl rounded-lg border p-5 sm:p-6 ${
        platformSettings.emergency_manual_deposit_auto_approve
          ? "border-rose-400/50 bg-rose-950/40"
          : "border-amber-400/25 bg-[#14110a]"
      }`}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3">
            <div className={`grid h-11 w-11 shrink-0 place-items-center rounded-lg border ${
              platformSettings.emergency_manual_deposit_auto_approve
                ? "border-rose-400/40 bg-rose-500/10 text-rose-300"
                : "border-amber-400/30 bg-amber-500/10 text-amber-200"
            }`}>
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-white">Emergency Chime/Venmo payment approval</h2>
              <p className="mt-1 text-sm leading-6 text-slate-300">
                When ON, new Chime and Venmo deposits submit immediately, then are credited after a random 15-45 second delay without verifying payment. Eligible Chime/Venmo withdrawals are also approved after 15-45 seconds: the wallet amount is reserved and routed to the active payout group. This does not send money; a payout agent must still pay and upload proof. Player-uploaded deposit proof is not payment verification. Fake deposits or fraudulent payout details can cause a loss.
              </p>
              <p className="mt-2 text-xs leading-5 text-slate-400">
                Deposit approval notices include the uploaded proof photo in Telegram. Applies only to new eligible requests while enabled. Withdrawal KYC, daily limits, available balance, and payout-group eligibility still apply. Cash App, existing pending requests, and withdrawals that cannot use the group stay on manual review. Turn it OFF when you return.
              </p>
              {platformSettings.emergency_manual_deposit_auto_approve && (
                <p className="mt-3 rounded-lg border border-rose-400/40 bg-rose-500/10 px-3 py-2 text-xs font-bold leading-5 text-rose-200">
                  Emergency auto-approval is active. Eligible deposits may be credited without payment verification; eligible withdrawals will reserve wallet funds for the payout group.
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            disabled={platformLoading || platformSaving}
            aria-pressed={platformSettings.emergency_manual_deposit_auto_approve}
            onClick={() => savePlatformSettings({
              ...platformSettings,
              emergency_manual_deposit_auto_approve: !platformSettings.emergency_manual_deposit_auto_approve,
            })}
            className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-black transition disabled:opacity-60 ${
              platformSettings.emergency_manual_deposit_auto_approve
                ? "border border-rose-400/50 bg-rose-500/20 text-rose-100 hover:bg-rose-500/30"
                : "border border-amber-400/35 bg-amber-500/10 text-amber-100 hover:bg-amber-500/20"
            }`}
          >
            {platformSettings.emergency_manual_deposit_auto_approve ? (
              <ToggleRight className="h-5 w-5" />
            ) : (
              <ToggleLeft className="h-5 w-5" />
            )}
            {platformLoading
              ? "Loading..."
              : platformSaving
                ? "Saving..."
                : platformSettings.emergency_manual_deposit_auto_approve
                  ? "Auto-approve ON"
                  : "Auto-approve OFF"}
          </button>
        </div>
      </div>

      <div className="max-w-2xl rounded-lg border border-[#332a10] bg-[#101010] p-5 sm:p-6">
        <div className="mb-5 flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-lg border border-[#2F6FA5]/35 bg-[#2F6FA5]/10 text-[#6EA8D8]">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-white">Admin password</h2>
            <p className="truncate text-sm text-slate-500">{user?.email || "Current admin"}</p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <PasswordField
            autoComplete="current-password"
            icon={Lock}
            label="Current password"
            onChange={(value) => setForm((current) => ({ ...current, old_password: value }))}
            value={form.old_password}
          />
          <PasswordField
            autoComplete="new-password"
            icon={KeyRound}
            label="New password"
            onChange={(value) => setForm((current) => ({ ...current, new_password: value }))}
            value={form.new_password}
          />
          <PasswordField
            autoComplete="new-password"
            icon={KeyRound}
            label="Confirm new password"
            onChange={(value) => setForm((current) => ({ ...current, confirm_password: value }))}
            value={form.confirm_password}
          />

          <button
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-full bg-[#2F6FA5] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#4E91C8] disabled:opacity-60"
            type="submit"
          >
            <Save className="h-4 w-4" />
            {saving ? "Saving..." : "Change password"}
          </button>
        </form>
      </div>
    </div>
  );
}

function PasswordField({ autoComplete, icon: Icon, label, onChange, value }) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      <span className="relative block">
        <Icon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
        <input
          autoComplete={autoComplete}
          className="h-11 w-full rounded-xl border border-white/10 bg-[#0d0d10] px-3 pl-9 text-sm text-slate-100 outline-none transition-colors placeholder:text-slate-500 focus:border-[#2F6FA5]/70"
          minLength={6}
          onChange={(event) => onChange(event.target.value)}
          required
          type="password"
          value={value}
        />
      </span>
    </label>
  );
}
